import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { RequestTypes, UnidadCotizacionServicio } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { SolicitudServicioDetail } from "@/services/service-requests.service";
import { AppColors } from "@/styles/colors";
import {
  closeSamplingRequest,
  fetchSamplingRequestDetail,
  markDoneSamplingRequest,
  rejectSamplingRequest,
} from "@/pages/ata-rsa-production/sampling-requests/detail/handler-functions";
import { Button, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { Check, X } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { SamplingRequestMainData } from "../components/SamplingRequestMainData";
import { ServiceRequestStatus } from "@/constants/entity-status";
import { PermissionCode } from "@/constants/permissions";

export const SamplingRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState<SolicitudServicioDetail | null>(null);

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();
  const [loadingReject, setLoadingReject] = useState(false);

  const { open: openDoneModal, setOpen: setOpenDoneModal } = useDisclosure();
  const [loadingDone, setLoadingDone] = useState(false);

  const [loadingClose, setLoadingClose] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const handleBack = () => {
    navigate(AppRoutes.SAMPLING_REQUESTS);
  };

  const fetchDetail = async (showLoad = true) =>
    fetchSamplingRequestDetail({
      id,
      showLoad,
      setLoadingDetail,
      setDetail,
      navigate,
      fetchPermissions,
    });

  useEffect(() => {
    if (isActive && id) {
      fetchDetail();
    }
  }, [isActive, id]);

  const isRejected = detail?.estado === ServiceRequestStatus.REJECTED;
  const isDone = detail?.estado === ServiceRequestStatus.DONE;
  const isClosed = detail?.estado === ServiceRequestStatus.CLOSED;

  const showCantidadMuestras =
    detail?.cantidadMuestras !== null &&
    detail?.cantidadMuestras !== undefined &&
    Number(detail.cantidadMuestras) > 0;

  const unidadCotizacionValue = useMemo(() => {
    const raw = Number(detail?.unidadCotizacion ?? 0);
    // Always use the value from backend if it exists (including 0 or any other value)
    // Only use fallback when unidadCotizacion is undefined or null
    if (detail?.unidadCotizacion !== undefined && detail?.unidadCotizacion !== null) {
      return raw;
    }
    return showCantidadMuestras
      ? UnidadCotizacionServicio.CantidadMuestra
      : UnidadCotizacionServicio.Hectarea;
  }, [detail?.unidadCotizacion, showCantidadMuestras]);

  const handleRejectRequest = (event?: FieldValues) =>
    rejectSamplingRequest({
      detailId: detail?.id ? Number(detail.id) : undefined,
      idParam: id,
      event,
      setLoadingReject,
      setOpenRejectModal,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleMarkDone = (values: {
    quantity: number;
    fechaRealizacion: string;
  }) =>
    markDoneSamplingRequest({
      detailId: detail?.id ? Number(detail.id) : undefined,
      unidadCotizacion: unidadCotizacionValue,
      idParam: id,
      values,
      setLoadingDone,
      setOpenDoneModal,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleCloseRequest = () =>
    closeSamplingRequest({
      detailId: detail?.id ? Number(detail.id) : undefined,
      idParam: id,
      setLoadingClose,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const isMuestrasUnidad = useMemo(
    () => unidadCotizacionValue === UnidadCotizacionServicio.CantidadMuestra,
    [unidadCotizacionValue],
  );

  const unidadLabel = useMemo(
    () => (isMuestrasUnidad ? "muestras" : "ha"),
    [isMuestrasUnidad],
  );

  const cantidadLabel = useMemo(
    () => (isMuestrasUnidad ? "Cantidad/Muestras" : "Hectáreas"),
    [isMuestrasUnidad],
  );

  const cantidadValue = useMemo(
    () => (isMuestrasUnidad ? detail?.cantidadMuestras : detail?.hectareas),
    [detail?.cantidadMuestras, detail?.hectareas, isMuestrasUnidad],
  );

  const doneModalQuantityLabel = useMemo(
    () => (isMuestrasUnidad ? "muestras" : "hectáreas"),
    [isMuestrasUnidad],
  );

  const doneModalDefaultQuantity = useMemo(
    () => (isMuestrasUnidad ? detail?.cantidadMuestras : detail?.hectareas),
    [detail?.cantidadMuestras, detail?.hectareas, isMuestrasUnidad],
  );

  const showExecutionInfo = isDone || isClosed;

  const executedLabel = useMemo(
    () => (isMuestrasUnidad ? "Muestras ejecutadas" : "Hectáreas ejecutadas"),
    [isMuestrasUnidad],
  );

  const executedValue = useMemo(
    () =>
      isMuestrasUnidad
        ? detail?.cantidadMuestrasEjecutadas
        : detail?.hectareasEjecutadas,
    [
      detail?.cantidadMuestrasEjecutadas,
      detail?.hectareasEjecutadas,
      isMuestrasUnidad,
    ],
  );

  const executedFormatted = Number.isFinite(Number(executedValue))
    ? `${new Intl.NumberFormat("es-AR").format(Number(executedValue))} ${unidadLabel}`
    : "-";

  const cantidadFormatted = Number.isFinite(Number(cantidadValue))
    ? `${new Intl.NumberFormat("es-AR").format(Number(cantidadValue))} ${unidadLabel}`
    : "-";

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Solicitud de muestreo"
        onBack={handleBack}
        backButton
        loading={loadingDetail || loadingReject || loadingDone || loadingClose}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="20px"
        >
          {detail && detail?.estado === ServiceRequestStatus.PENDING && hasWriteAccess(PermissionCode.SAMPLING_REQUESTS) && (
            <Flex justifyContent="flex-end" gap="12px">
              <Button
                paddingX="12px"
                height="32px"
                bgColor={AppColors.LIGHT_GREEN}
                disabled={loadingReject || loadingDone || loadingClose}
                onClick={() => setOpenDoneModal(true)}
              >
                <Check />
                <Text fontWeight={600} fontSize="14px">
                  Marcar realizado
                </Text>
              </Button>

              <Button
                paddingX="12px"
                height="32px"
                bgColor={AppColors.RED_BUTTON_ALT}
                disabled={loadingReject || loadingDone || loadingClose}
                onClick={() => setOpenRejectModal(true)}
              >
                <X />
                <Text
                  fontWeight={600}
                  fontSize="14px"
                  color={AppColors.WHITE_SECONDARY}
                >
                  Rechazar
                </Text>
              </Button>
            </Flex>
          )}

          {detail?.estado === ServiceRequestStatus.DONE && hasWriteAccess(PermissionCode.SAMPLING_REQUESTS) && (
            <Flex justifyContent="flex-end" gap="12px">
              <Button
                paddingX="12px"
                height="32px"
                bgColor={AppColors.BLACK_TEXT_SECONDARY}
                disabled={loadingReject || loadingDone || loadingClose}
                onClick={handleCloseRequest}
              >
                <Check />
                <Text fontWeight={600} fontSize="14px">
                  Cerrar
                </Text>
              </Button>
            </Flex>
          )}

          {detail && (
            <SamplingRequestMainData
              detail={detail}
              cantidadLabel={cantidadLabel}
              cantidadFormatted={cantidadFormatted}
              executedLabel={executedLabel}
              executedFormatted={executedFormatted}
              showExecutionInfo={showExecutionInfo}
              isRejected={isRejected}
              isMuestrasUnidad={isMuestrasUnidad}
            />
          )}
        </Flex>
      )}

      {detail?.id && (
        <Suspense>

          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectRequest}
            loadingAccept={loadingReject}
            idTipoSolicitud={RequestTypes.Muestreo}
            entityName="solicitud"
          />
        </Suspense>
      )}

      {detail?.id && (
        <Suspense>
          <MarkDoneSamplingRequestModalDynamic
            open={openDoneModal}
            setOpen={setOpenDoneModal}
            quantityLabel={doneModalQuantityLabel}
            defaultQuantity={doneModalDefaultQuantity}
            handleAccept={handleMarkDone}
            loadingAccept={loadingDone}
            isMuestrasUnidad={isMuestrasUnidad}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const MarkDoneSamplingRequestModalDynamic = lazy(() =>
  import("@/components/modal/MarkDoneSamplingRequestModal").then((el) => ({
    default: el.MarkDoneSamplingRequestModal,
  })),
);

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el.RejectRequestModal,
  })),
);
